import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  AddModeratorOutlined,
  DeleteOutline,
  EditOutlined,
  SecurityOutlined,
  ShieldOutlined,
} from "@mui/icons-material";
import {
  createRole,
  deleteRole,
  listPermissions,
  listRoles,
  listRoleTemplates,
  updateRole,
} from "../api/agentApi";
import { isAdminUser, useUser } from "./UserContext.jsx";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";
import { PERMISSIONS } from "../permissions";

type Permission = {
  id: string;
  group_key: string;
  label: string;
  description: string;
  risk_level: string;
  sort_order: number;
};

type Role = {
  id: string;
  tenant_id: string | null;
  slug: string;
  name: string;
  description?: string | null;
  is_builtin: boolean;
  is_system: boolean;
  permission_ids: string[];
};

type RoleTemplate = {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  permission_ids: string[];
};

const borderSoft = "#E5D4C4";
const NO_TEMPLATE_VALUE = "__no_template__";

const primaryActionButtonSx = {
  borderRadius: UI_RADIUS.button,
  bgcolor: "primary.main",
  boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
  color: "primary.contrastText",
  textTransform: "none",
  fontWeight: 900,
  minHeight: 44,
  px: 2.4,
  "&:hover": {
    bgcolor: "primary.dark",
    boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}`,
  },
};

const secondaryActionButtonSx = {
  borderRadius: UI_RADIUS.button,
  textTransform: "none",
  fontWeight: 850,
};

const groupLabel = (groupKey: string) =>
  groupKey
    .split("_")
    .join(" ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const sortByName = <T extends { name: string }>(items: T[]) =>
  [...items].sort((a, b) => a.name.localeCompare(b.name));

const RolesPermissionsPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { user, can } = useUser();
  const canReadRoles = Boolean(can?.(PERMISSIONS.ROLES_READ));
  const canManageRoles = Boolean(can?.(PERMISSIONS.ROLES_MANAGE));
  const canViewSystemRoles = isAdminUser(user);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [templates, setTemplates] = useState<RoleTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
  const [selectedTemplateSlug, setSelectedTemplateSlug] = useState("");
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [permissionCatalogOpen, setPermissionCatalogOpen] = useState(false);

  const copperText = isDark ? "#F2C69B" : BRAND_COLORS.primary.dark;
  const panelBorder = isDark ? "rgba(224,177,132,0.24)" : borderSoft;
  const panelShadow = isDark ? "0 18px 54px rgba(0,0,0,0.36)" : "0 16px 44px rgba(20,18,15,0.06)";
  const headerCellSx = {
    bgcolor: isDark ? "rgba(224,177,132,0.10)" : "#F7F2ED",
    fontWeight: 900,
    color: "text.secondary",
  };

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [permissionResponse, roleResponse, templateResponse] = await Promise.all([
        listPermissions(),
        listRoles(),
        listRoleTemplates(),
      ]);
      setPermissions(permissionResponse.permissions || []);
      setRoles(roleResponse.roles || []);
      setTemplates(templateResponse.role_templates || []);
    } catch (err: any) {
      setError(err?.message || "Failed to load roles and permissions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && canReadRoles) {
      loadData();
    }
  }, [user, canReadRoles]);

  const groupedPermissions = useMemo(() => {
    const grouped = permissions.reduce<Record<string, Permission[]>>((acc, permission) => {
      acc[permission.group_key] = acc[permission.group_key] || [];
      acc[permission.group_key].push(permission);
      return acc;
    }, {});
    return Object.entries(grouped)
      .map(([groupKey, items]) => ({
        groupKey,
        permissions: [...items].sort((a, b) => a.sort_order - b.sort_order),
      }))
      .sort((a, b) => {
        const firstA = a.permissions[0]?.sort_order ?? 0;
        const firstB = b.permissions[0]?.sort_order ?? 0;
        return firstA - firstB;
      });
  }, [permissions]);

  const visibleRoles = roles.filter((role) => canViewSystemRoles || !role.is_system);
  const builtInRoleCount = visibleRoles.filter((role) => role.is_builtin && !role.is_system).length;
  const customRoleCount = visibleRoles.filter((role) => !role.is_builtin && !role.is_system).length;
  const highRiskPermissionCount = permissions.filter((permission) =>
    ["high", "critical"].includes(permission.risk_level),
  ).length;

  if (!user || !canReadRoles) {
    return (
      <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
        <Alert severity="error">Access denied. You do not have permission to view roles.</Alert>
      </Box>
    );
  }

  const resetDialog = () => {
    setDialogOpen(false);
    setEditingRole(null);
    setError("");
    setRoleName("");
    setRoleDescription("");
    setSelectedPermissionIds([]);
    setSelectedTemplateSlug("");
  };

  const openCreateDialog = (template?: RoleTemplate) => {
    setError("");
    setEditingRole(null);
    setRoleName("");
    setRoleDescription(template?.description || "");
    setSelectedPermissionIds(template?.permission_ids || []);
    setSelectedTemplateSlug(template?.slug || "");
    setDialogOpen(true);
  };

  const openEditDialog = (role: Role) => {
    setError("");
    setEditingRole(role);
    setRoleName(role.name);
    setRoleDescription(role.description || "");
    setSelectedPermissionIds(role.permission_ids || []);
    setSelectedTemplateSlug("");
    setDialogOpen(true);
  };

  const handleTemplateChange = (slug: string) => {
    setSelectedTemplateSlug(slug);
    const template = templates.find((item) => item.slug === slug);
    if (!template) return;
    setSelectedPermissionIds(template.permission_ids);
    setRoleDescription((current) => current || template.description || "");
  };

  const togglePermission = (permissionId: string) => {
    setSelectedPermissionIds((current) =>
      current.includes(permissionId)
        ? current.filter((id) => id !== permissionId)
        : [...current, permissionId],
    );
  };

  const handleSubmitRole = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        name: roleName.trim(),
        description: roleDescription.trim() || null,
        permission_ids: selectedPermissionIds,
      };
      if (editingRole) {
        await updateRole(editingRole.id, payload);
        setSuccess(`Role "${payload.name}" updated.`);
      } else {
        await createRole(payload);
        setSuccess(`Role "${payload.name}" created.`);
      }
      resetDialog();
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to save role.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await deleteRole(roleToDelete.id);
      setSuccess(`Role "${roleToDelete.name}" deleted.`);
      setRoleToDelete(null);
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to delete role.");
    } finally {
      setSaving(false);
    }
  };

  const roleKindChip = (role: Role) => {
    if (role.is_system) return <Chip label="System" size="small" color="default" />;
    if (role.is_builtin) return <Chip label="Built-in" size="small" color="success" variant="outlined" />;
    return <Chip label="Custom" size="small" color="warning" variant="outlined" />;
  };

  return (
    <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto", color: "text.primary" }}>
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box>
          <Typography variant="h3" component="h1" sx={PAGE_TYPOGRAPHY.heading}>
            Roles
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.35 }}>
            Create custom roles from the permission catalog and manage tenant access.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ pt: 0.2 }}>
          {canManageRoles && (
            <Button
              variant="contained"
              startIcon={<AddModeratorOutlined />}
              sx={primaryActionButtonSx}
              onClick={() => openCreateDialog()}
            >
              Create role
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<ShieldOutlined />}
            sx={{
              ...secondaryActionButtonSx,
              borderColor: panelBorder,
              color: copperText,
              minHeight: 44,
              px: 2,
            }}
            onClick={() => setPermissionCatalogOpen(true)}
          >
            Permission catalog
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={PAGE_SHELL.sectionGap} sx={{ mb: PAGE_SHELL.sectionGap }}>
        {[
          { label: "Built-in Roles", value: builtInRoleCount, icon: <SecurityOutlined /> },
          { label: "Custom roles", value: customRoleCount, icon: <AddModeratorOutlined /> },
          { label: "Permissions", value: permissions.length, icon: <ShieldOutlined /> },
          { label: "High risk", value: highRiskPermissionCount, icon: <ShieldOutlined /> },
        ].map((item) => (
          <Paper
            key={item.label}
            elevation={0}
            sx={{
              flex: 1,
              p: 2,
              borderRadius: UI_RADIUS.card,
              border: `1px solid ${panelBorder}`,
              bgcolor: "background.paper",
              boxShadow: panelShadow,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: UI_RADIUS.panel,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: isDark ? "rgba(224,177,132,0.14)" : BRAND_COLORS.primary.soft,
                  color: copperText,
                }}
              >
                {item.icon}
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={950}>
                  {item.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>

      {loading ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: UI_RADIUS.panel,
            border: `1px solid ${panelBorder}`,
            bgcolor: "background.paper",
          }}
        >
          <CircularProgress />
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            Loading roles...
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={PAGE_SHELL.sectionGap}>
          {templates.length > 0 && canManageRoles && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: UI_RADIUS.panel,
                border: `1px solid ${panelBorder}`,
                bgcolor: "background.paper",
                boxShadow: panelShadow,
              }}
            >
              <Typography variant="h6" fontWeight={950}>
                Start from a template
              </Typography>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                {sortByName(templates).map((template) => (
                  <Button
                    key={template.id}
                    variant="outlined"
                    onClick={() => openCreateDialog(template)}
                    sx={{
                      ...secondaryActionButtonSx,
                      justifyContent: "flex-start",
                      borderColor: panelBorder,
                      color: copperText,
                      px: 1.75,
                      py: 1.1,
                    }}
                  >
                    {template.name}
                  </Button>
                ))}
              </Stack>
            </Paper>
          )}

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: UI_RADIUS.panel,
              border: `1px solid ${panelBorder}`,
              bgcolor: "background.paper",
              boxShadow: panelShadow,
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Role</TableCell>
                  <TableCell sx={headerCellSx}>Type</TableCell>
                  <TableCell sx={headerCellSx}>Permissions</TableCell>
                  <TableCell sx={headerCellSx}>Description</TableCell>
                  {canManageRoles && (
                    <TableCell sx={headerCellSx} align="right">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortByName(visibleRoles).map((role) => {
                  const canEditRole = canManageRoles && !role.is_builtin && !role.is_system;
                  return (
                    <TableRow
                      key={role.id}
                      hover
                      sx={{
                        "&:last-child td": { borderBottom: 0 },
                        "&:hover": { bgcolor: isDark ? "rgba(224,177,132,0.08)" : "rgba(184,115,51,0.04)" },
                      }}
                    >
                      <TableCell>
                        <Typography fontWeight={900}>{role.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {role.slug}
                        </Typography>
                      </TableCell>
                      <TableCell>{roleKindChip(role)}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${role.permission_ids.length} permissions`}
                          size="small"
                          sx={{
                            borderRadius: UI_RADIUS.chip,
                            bgcolor: isDark ? "rgba(224,177,132,0.14)" : BRAND_COLORS.primary.soft,
                            color: copperText,
                            fontWeight: 850,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary", maxWidth: 520 }}>
                        {role.description || "No description"}
                      </TableCell>
                      {canManageRoles && (
                        <TableCell align="right">
                          {canEditRole && (
                            <>
                              <Tooltip title="Edit role">
                                <IconButton
                                  size="small"
                                  aria-label="Edit role"
                                  onClick={() => openEditDialog(role)}
                                  sx={{
                                    color: copperText,
                                    border: `1px solid ${panelBorder}`,
                                    borderRadius: UI_RADIUS.iconButton,
                                    mr: 0.75,
                                  }}
                                >
                                  <EditOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete role">
                                <IconButton
                                  size="small"
                                  aria-label="Delete role"
                                  onClick={() => {
                                    setError("");
                                    setRoleToDelete(role);
                                  }}
                                  sx={{
                                    color: "#A6423B",
                                    border: "1px solid rgba(166,66,59,0.22)",
                                    borderRadius: UI_RADIUS.iconButton,
                                  }}
                                >
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={resetDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 950 }}>
          {editingRole ? "Edit role" : "Create role"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}
            {!editingRole && templates.length > 0 && (
              <FormControl fullWidth>
                <InputLabel id="role-template-label">Template</InputLabel>
                <Select
                  labelId="role-template-label"
                  label="Template"
                  value={selectedTemplateSlug || NO_TEMPLATE_VALUE}
                  onChange={(event) => {
                    const value = String(event.target.value);
                    handleTemplateChange(value === NO_TEMPLATE_VALUE ? "" : value);
                  }}
                  renderValue={(value) => {
                    const slug = String(value);
                    if (!slug || slug === NO_TEMPLATE_VALUE) return "No Template";
                    return templates.find((template) => template.slug === slug)?.name || "No Template";
                  }}
                  sx={{ borderRadius: UI_RADIUS.input }}
                >
                  <MenuItem value={NO_TEMPLATE_VALUE}>No Template</MenuItem>
                  {sortByName(templates).map((template) => (
                    <MenuItem key={template.id} value={template.slug}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label="Role name"
              value={roleName}
              onChange={(event) => setRoleName(event.target.value)}
              fullWidth
              required
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: UI_RADIUS.input } }}
            />
            <TextField
              label="Description"
              value={roleDescription}
              onChange={(event) => setRoleDescription(event.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: UI_RADIUS.input } }}
            />
            <Box>
              <Typography fontWeight={950} sx={{ mb: 1 }}>
                Permissions
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.5,
                }}
              >
                {groupedPermissions.map((group) => (
                  <Box
                    key={group.groupKey}
                    sx={{
                      p: 1.25,
                      borderRadius: UI_RADIUS.panel,
                      border: `1px solid ${panelBorder}`,
                    }}
                  >
                    <Typography variant="body2" fontWeight={950} sx={{ mb: 0.5 }}>
                      {groupLabel(group.groupKey)}
                    </Typography>
                    <Stack>
                      {group.permissions.map((permission) => (
                        <FormControlLabel
                          key={permission.id}
                          control={
                            <Checkbox
                              checked={selectedPermissionIds.includes(permission.id)}
                              onChange={() => togglePermission(permission.id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={850}>
                                {permission.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {permission.id}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={resetDialog} sx={secondaryActionButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving || roleName.trim().length < 2 || selectedPermissionIds.length === 0}
            onClick={handleSubmitRole}
            sx={primaryActionButtonSx}
          >
            {saving ? "Saving..." : "Save role"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(roleToDelete)} onClose={() => setRoleToDelete(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 950 }}>Delete role</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          <Typography color="text.secondary">
            Delete <strong>{roleToDelete?.name}</strong>? Existing assignments will be removed from users.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleToDelete(null)} sx={secondaryActionButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={saving}
            onClick={handleDeleteRole}
            sx={{ borderRadius: UI_RADIUS.button, textTransform: "none", fontWeight: 900 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={permissionCatalogOpen} onClose={() => setPermissionCatalogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 950 }}>Permission catalog</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" },
              gap: 1.5,
              pt: 1,
            }}
          >
            {groupedPermissions.map((group) => (
              <Box
                key={group.groupKey}
                sx={{
                  p: 1.5,
                  borderRadius: UI_RADIUS.panel,
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(29,29,31,0.08)"}`,
                  bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(250,247,244,0.72)",
                }}
              >
                <Typography fontWeight={950} sx={{ mb: 1 }}>
                  {groupLabel(group.groupKey)}
                </Typography>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  {group.permissions.map((permission) => (
                    <Tooltip key={permission.id} title={permission.description}>
                      <Chip
                        label={permission.label}
                        size="small"
                        sx={{
                          borderRadius: UI_RADIUS.chip,
                          fontWeight: 800,
                          bgcolor: ["high", "critical"].includes(permission.risk_level)
                            ? alpha(theme.palette.warning.main, isDark ? 0.22 : 0.13)
                            : alpha(theme.palette.success.main, isDark ? 0.18 : 0.10),
                        }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPermissionCatalogOpen(false)} sx={secondaryActionButtonSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesPermissionsPage;
